import { useState, useEffect, useCallback } from 'react';
import { ChevronUp, ChevronDown } from 'lucide-react';
import { Button } from '../components/ui/button';

export default function ScrollButton() {
  const [scrollDirection, setScrollDirection] = useState(null); // 'up' or 'down'
  const [visible, setVisible] = useState(false);

  const handleScroll = useCallback(() => {
    const scrollTop = window.scrollY;
    const scrollHeight = document.documentElement.scrollHeight;
    const clientHeight = window.innerHeight;
    const nearTop = scrollTop < 200;
    const nearBottom = scrollTop + clientHeight >= scrollHeight - 200;

    if (nearTop && nearBottom) {
      // Page is too short to need scroll buttons
      setVisible(false);
      return;
    }

    if (nearTop) {
      setScrollDirection('down');
      setVisible(true);
    } else if (nearBottom) {
      setScrollDirection('up');
      setVisible(true);
    } else {
      // In the middle — show based on last scroll direction
      setVisible(true);
    }
  }, []);

  // Track scroll direction while in the middle of the page
  useEffect(() => {
    let lastScrollY = window.scrollY;
    let ticking = false;

    const onScroll = () => {
      if (!ticking) {
        window.requestAnimationFrame(() => {
          const currentScrollY = window.scrollY;
          const scrollHeight = document.documentElement.scrollHeight;
          const clientHeight = window.innerHeight;
          const nearTop = currentScrollY < 200;
          const nearBottom = currentScrollY + clientHeight >= scrollHeight - 200;

          if (nearTop && nearBottom) {
            setVisible(false);
          } else if (nearTop) {
            setScrollDirection('down');
            setVisible(true);
          } else if (nearBottom) {
            setScrollDirection('up');
            setVisible(true);
          } else {
            setVisible(true);
            if (currentScrollY > lastScrollY) {
              setScrollDirection('down');
            } else if (currentScrollY < lastScrollY) {
              setScrollDirection('up');
            }
          }

          lastScrollY = currentScrollY;
          ticking = false;
        });
        ticking = true;
      }
    };

    window.addEventListener('scroll', onScroll, { passive: true });
    // Run once on mount
    handleScroll();

    return () => window.removeEventListener('scroll', onScroll);
  }, [handleScroll]);

  const scrollToTop = () => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const scrollToBottom = () => {
    window.scrollTo({ top: document.documentElement.scrollHeight, behavior: 'smooth' });
  };

  if (!visible || !scrollDirection) return null;

  return (
    <Button
      variant="outline"
      size="icon"
      onClick={scrollDirection === 'up' ? scrollToTop : scrollToBottom}
      className="fixed right-4 bottom-20 z-40 h-10 w-10 rounded-full shadow-lg bg-card/90 backdrop-blur-sm border-border/60 hover:bg-primary hover:text-primary-foreground transition-all"
      style={{ bottom: 'max(5rem, calc(5rem + env(safe-area-inset-bottom, 0px)))' }}
      aria-label={scrollDirection === 'up' ? 'Scroll to top' : 'Scroll to bottom'}
    >
      {scrollDirection === 'up' ? (
        <ChevronUp className="w-5 h-5" />
      ) : (
        <ChevronDown className="w-5 h-5" />
      )}
    </Button>
  );
}
